import config from "@/lib/config";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import { setLoading, setDays } from "@/redux/slices/month.slice";
import { format, lastDayOfMonth, isAfter, startOfMonth } from "date-fns";
import { enUS } from "date-fns/locale";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

const useOnMonthChange = (initialDate: Date, bookingId?: string) => {
  const { unit } = useParams();
  const unitId = useMemo(() => unit, [unit]);
  const dispatch = useDispatch();
  const lastFetchedMonth = useRef<string | null>(null);

  async function checkAvailability(values: {
    startDate: string;
    endDate: string;
  }) {
    const params = {
      checkInDate: format(new Date(values.startDate), "yyyy-MM-dd"),
      checkOutDate: format(new Date(values.endDate), "yyyy-MM-dd"),
    };

    if (bookingId) Object.assign(params, { bookingId });

    try {
      const res = await ehjzleeInstance.get(
        `${config.props}/${unitId}/events/availability${
          bookingId ? `/${bookingId}` : ""
        }`,
        { params }
      );
      return res.data.isAvailable;
    } catch (error) {
      console.log(error);
    }
  }

  const fetchData = async (date: Date) => {
    const year = date.getFullYear();
    const month = date.toLocaleString("en-US", { month: "long" });
    const monthKey = `${year}-${month}`;

    // Only fetch if this is a new month
    if (lastFetchedMonth.current === monthKey) {
      return;
    }

    try {
      dispatch(setLoading(true));
      const currentDate = new Date();
      const monthStart = startOfMonth(date);

      // Use current datetime as start date if the month is current month, otherwise use month start
      const startDate = isAfter(monthStart, currentDate)
        ? format(monthStart, "yyyy-MM-dd", { locale: enUS })
        : format(currentDate, "yyyy-MM-dd", { locale: enUS });

      const params = {
        startDate,
        endDate: format(
          lastDayOfMonth(new Date(`${month} 1, ${year}`)),
          "yyyy-MM-dd",
          { locale: enUS }
        ),
      };

      const pricingRes = await ehjzleeInstance.get(
        `${config.props}/${unitId}/pricing`,
        { params }
      );

      // Only fetch availability if this is a new month
      const availability = await checkAvailability(params);

      // Create a map of availability status for quick lookup
      const availabilityMap = new Map(
        availability?.map((item: any) => [
          format(new Date(item.date), "yyyy-MM-dd"),
          item.isAvailable
        ]) || []
      );

      // Generate all days for the month, not just pricing data
      const allDaysInMonth = [];
      const startOfMonthDate = new Date(year, date.getMonth(), 1);
      const endOfMonthDate = new Date(year, date.getMonth() + 1, 0);

      for (let d = new Date(startOfMonthDate); d <= endOfMonthDate; d.setDate(d.getDate() + 1)) {
        const dayDateStr = format(d, "yyyy-MM-dd");
        const isAvailable = availabilityMap.get(dayDateStr) ?? false;

        // Find pricing data for this day if it exists
        const pricingData = pricingRes.data.prices.find((priceDay: any) =>
          format(new Date(priceDay.date), "yyyy-MM-dd") === dayDateStr
        );

        allDaysInMonth.push({
          date: d.toISOString(),
          currentValue: pricingData?.currentValue || 0,
          isAvailable: isAvailable,
          ...pricingData, // Include any other pricing data
        });
      }

      console.log("All days in month:", allDaysInMonth);

      dispatch(setDays(allDaysInMonth));

      lastFetchedMonth.current = monthKey;
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchData(initialDate);
  }, []);

  return fetchData;
};

export default useOnMonthChange;
